import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MoreHorizontalIcon, PlusIcon, ChevronRightIcon, BuildingIcon, UserIcon, LandmarkIcon, SmartphoneIcon, CreditCardIcon, WalletIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { mockSuppliers } from '../mocks/mockSuppliers';
export function SupplierDetail() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const supplier = mockSuppliers.find(s => s.id === id) || mockSuppliers[0];
  const fullAddress = [supplier.addressLine1, supplier.addressLine2, supplier.city, supplier.province, supplier.postcode, supplier.country].filter(Boolean).join(', ');
  const billingEmail = supplier.useAsBillingEmail ? supplier.email : `${supplier.email} (separate billing)`;
  const bills = {
    total: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    due: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    awaitingApproval: 0,
    scheduled: 0,
    paid: 0
  };
  const history = [{
    id: 'h1',
    action: `${supplier.name} was created`,
    date: supplier.dateCreated.replace(',', ' at')
  }];
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-13488">
      {/* Header */}
      <div className="flex items-center" data-id="element-13489">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-13490">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-13491" />
          {t('pages.SupplierDetail.all_suppliers')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-13492">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-13493">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-13494">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13495">
              <div className="flex justify-between items-start" data-id="element-13496">
                <div data-id="element-13497">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-13498">
                    {supplier.name}
                  </h1>
                  <div className="flex items-center text-sm" data-id="element-13499">
                    <span className="text-gray-500 mr-2" data-id="element-13500">
                      {t('pages.SupplierDetail.you_owe_them')}
                    </span>
                    <span className="px-2.5 py-0.5 bg-[#E2231A] text-white text-xs font-semibold rounded-md shadow-sm" data-id="element-13501">
                      {supplier.amountOwed}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3" data-id="element-13502">
                  <button onClick={() => navigate(`/bill-form?supplierId=${supplier.id}`)} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13503">
                    {t('pages.SupplierDetail.add_bill')}
                  </button>
                  <button aria-label="More actions" className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-13504">
                    <MoreHorizontalIcon size={20} aria-hidden="true" data-id="element-13505" />
                  </button>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13506">
              <div className="flex justify-between items-center mb-6" data-id="element-13507">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13508">
                  {t('pages.SupplierDetail.details')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-13509">
                  {t('pages.SupplierDetail.edit')}
                </button>
              </div>

              <div className="space-y-4" data-id="element-13510">
                {/* Type */}
                <div className="grid grid-cols-3 gap-4 border-b border-gray-50 pb-4" data-id="element-13511">
                  <div className="text-sm text-gray-500" data-id="element-13512">
                    {t('pages.BulkSupplierUpload.type')}
                  </div>
                  <div className="col-span-2" data-id="element-13513">
                    {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-13514">
                        <BuildingIcon size={12} className="mr-1.5" data-id="element-13515" />
                        {t('pages.SupplierDetail.company')}
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-13516">
                        <UserIcon size={12} className="mr-1.5" data-id="element-13517" />
                        {t('pages.SupplierDetail.individual')}
                      </span>}
                  </div>
                </div>

                {/* Company-specific fields */}
                {supplier.supplierType === 'company' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13518">
                      <div className="text-sm text-gray-500" data-id="element-13519">
                        {t('pages.SupplierDetail.company_name_local')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13520">
                        {supplier.companyNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13521">
                      <div className="text-sm text-gray-500" data-id="element-13522">
                        {t('pages.SupplierDetail.company_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13523">
                        {supplier.companyNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13524">
                      <div className="text-sm text-gray-500" data-id="element-13525">
                        {t('pages.SupplierDetail.tax_id_registration_no')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13526">
                        {supplier.taxId || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13527">
                      <div className="text-sm text-gray-500" data-id="element-13528">
                        {t('pages.SupplierDetail.branch')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13529">
                        {supplier.branch === '00000' ? '00000 (Head Office)' : supplier.branch || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13530">
                      <div className="text-sm text-gray-500" data-id="element-13531">
                        {t('pages.SupplierDetail.vat_registered')}
                      </div>
                      <div className="col-span-2" data-id="element-13532">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${supplier.vatRegistered ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-13533">
                          {supplier.vatRegistered ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    {supplier.contactPerson && <div className="grid grid-cols-3 gap-4" data-id="element-13534">
                        <div className="text-sm text-gray-500" data-id="element-13535">
                          {t('pages.SupplierDetail.contact_person')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13536">
                          {supplier.contactPerson}
                        </div>
                      </div>}
                  </>}

                {/* Individual-specific fields */}
                {supplier.supplierType === 'individual' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13537">
                      <div className="text-sm text-gray-500" data-id="element-13538">
                        {t('pages.SupplierDetail.full_name_thai')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13539">
                        {supplier.fullNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13540">
                      <div className="text-sm text-gray-500" data-id="element-13541">
                        {t('pages.SupplierDetail.full_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13542">
                        {supplier.fullNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13543">
                      <div className="text-sm text-gray-500" data-id="element-13544">
                        {t('pages.SupplierDetail.id_type')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 capitalize" data-id="element-13545">
                        {supplier.idType === 'citizen_id' ? 'Citizen ID' : supplier.idType === 'passport' ? 'Passport' : '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13546">
                      <div className="text-sm text-gray-500" data-id="element-13547">
                        {t('pages.SupplierDetail.id_number')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13548">
                        {supplier.idNumber || '—'}
                      </div>
                    </div>
                    {supplier.individualTaxId && <div className="grid grid-cols-3 gap-4" data-id="element-13549">
                        <div className="text-sm text-gray-500" data-id="element-13550">
                          {t('pages.BulkSupplierUpload.tax_id')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13551">
                          {supplier.individualTaxId}
                        </div>
                      </div>}
                  </>}

                {/* Shared fields */}
                <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4" data-id="element-13552">
                  <div className="text-sm text-gray-500" data-id="element-13553">
                    {t('pages.BulkSupplierUpload.email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13554">
                    {supplier.email}
                  </div>
                </div>
                {supplier.phone && <div className="grid grid-cols-3 gap-4" data-id="element-13555">
                    <div className="text-sm text-gray-500" data-id="element-13556">
                      {t('pages.BulkSupplierUpload.phone')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13557">
                      {supplier.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-13558">
                  <div className="text-sm text-gray-500" data-id="element-13559">
                    {t('pages.SupplierDetail.billing_email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13560">
                    {billingEmail}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-13561">
                  <div className="text-sm text-gray-500" data-id="element-13562">
                    {t('pages.SupplierDetail.address')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13563">
                    {fullAddress || '—'}
                  </div>
                </div>
                {supplier.internalReference && <div className="grid grid-cols-3 gap-4" data-id="element-13564">
                    <div className="text-sm text-gray-500" data-id="element-13565">
                      {t('pages.SupplierDetail.internal_reference')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13566">
                      {supplier.internalReference}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Pay-in Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13567">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-13568">
                {t('pages.SupplierDetail.payin_method')}
              </h3>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-13569">
                <div className="flex items-center" data-id="element-13570">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${supplier.payInMethod === 'acquiring' ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-13571">
                    {supplier.payInMethod === 'acquiring' ? <WalletIcon size={20} className="text-green-700" data-id="element-13572" /> : <CreditCardIcon size={20} className="text-blue-700" data-id="element-13573" />}
                  </div>
                  <div data-id="element-13574">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-13575">
                      {supplier.payInMethod === 'acquiring' ? 'Acquiring Volume' : 'Card Payment'}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-13576">
                      {supplier.payInMethod === 'acquiring' ? 'Funded from merchant acquiring settlement balance' : 'Charged to linked card'}
                    </div>
                  </div>
                </div>

                {supplier.payInMethod === 'card' && supplier.cardNumber && <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13577">
                    <div data-id="element-13578">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-13579">
                        {t('pages.SupplierDetail.cardholder')}
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-13580">
                        {supplier.cardholderName}
                      </div>
                    </div>
                    <div data-id="element-13581">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-13582">
                        {t('pages.SupplierDetail.card_number')}
                      </div>
                      <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13583">
                        {supplier.cardNumber}
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            {/* Payout Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13584">
              <div className="flex justify-between items-center mb-6" data-id="element-13585">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13586">
                  {t('pages.SupplierDetail.payout_method')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-13587">
                  {t('pages.SupplierDetail.edit_payout_method')}
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-13588">
                {supplier.payoutMethod === 'bank_account' ? <>
                    <div className="flex items-center mb-4" data-id="element-13589">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-4" data-id="element-13590">
                        <LandmarkIcon size={20} className="text-green-700" data-id="element-13591" />
                      </div>
                      <div data-id="element-13592">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-13593">
                          {t('pages.SupplierDetail.bank_account')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-13594">
                          {t('pages.SupplierDetail.direct_transfer')}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13595">
                      <div data-id="element-13596">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13597">
                          {t('pages.SupplierDetail.bank_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13598">
                          {supplier.bankName}
                        </div>
                      </div>
                      <div data-id="element-13599">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13600">
                          {t('pages.SupplierDetail.account_number')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13601">
                          {'***' + supplier.accountNumber.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-13602">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13603">
                          {t('pages.SupplierDetail.account_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13604">
                          {supplier.accountName}
                        </div>
                      </div>
                    </div>
                  </> : <>
                    <div className="flex items-center mb-4" data-id="element-13605">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-4" data-id="element-13606">
                        <SmartphoneIcon size={20} className="text-purple-700" data-id="element-13607" />
                      </div>
                      <div data-id="element-13608">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-13609">
                          {t('pages.SupplierDetail.promptpay')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-13610">
                          {supplier.promptPayType === 'phone' ? 'Phone number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13611">
                      <div data-id="element-13612">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13613">
                          {t('pages.SupplierDetail.promptpay_id')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13614">
                          {'***' + supplier.promptPayId.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-13615">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13616">
                          {t('pages.SupplierDetail.display_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13617">
                          {supplier.promptPayName}
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
            </div>

            {/* Source Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13618">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-13619">
                {t('pages.SupplierDetail.source')}
              </h3>
              <div className="space-y-4" data-id="element-13620">
                <div className="grid grid-cols-3 gap-4" data-id="element-13621">
                  <div className="text-sm text-gray-500" data-id="element-13622">
                    {t('pages.SupplierDetail.platform')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13623">
                    {supplier.source}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-13624">
                  <div className="text-sm text-gray-500" data-id="element-13625">
                    {t('pages.SupplierDetail.date_created')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13626">
                    {supplier.dateCreated}
                  </div>
                </div>
              </div>
            </div>

            {/* Bills Section */}
            <div className="p-8" data-id="element-13627">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-13628">
                {t('pages.SupplierDetail.bills')}
              </h3>
              <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors group" data-id="element-13629">
                <div data-id="element-13630">
                  <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-13631">
                    {bills.total} {t('pages.SupplierDetail.bill')}
                    {bills.total !== 1 ? 's' : ''}{' '}
                    {t('pages.SupplierDetail.for')} {supplier.name}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-13632">
                    {bills.due} {t('pages.SupplierDetail.due')}{' '}
                    {bills.awaitingApproval}{' '}
                    {t('pages.SupplierDetail.awaiting_approval')}{' '}
                    {bills.scheduled} {t('pages.SupplierDetail.scheduled_and')}{' '}
                    {bills.paid} {t('pages.SupplierDetail.paid')}
                  </div>
                </div>
                <ChevronRightIcon size={20} className="text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-13633" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-13634">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-13635">
            {t('pages.SupplierDetail.history')}
          </h3>
          <div className="space-y-6" data-id="element-13636">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-13637">
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-13638"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-13639">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-13640" />
                </div>
                <div data-id="element-13641">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13642">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-13643">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}